/**
 * DTCG ("Design Tokens Community Group", the W3C group whose Design Tokens
 * Format Module standardizes the `{ $value, $type }` token shape:
 * https://tr.designtokens.org/format/) token helpers. Style Dictionary reads
 * this shape when `usesDtcg: true` (see exporter/build-tokens.mjs).
 *
 * The typed token tree (`TOKENS` in index.ts) is the source
 * of truth: each token declares its `$type` explicitly, so the exporter never
 * has to sniff a value's kind to decide how to emit it. The public `DS_*`
 * exports are derived from that tree by `values()`, which preserves each token's
 * literal value type — so consumers (and the one `as DS_BREAKPOINT` type-position
 * use) compile unchanged.
 *
 * `$type` conventions used here:
 *   - `'color'`, `'dimension'`, `'fontWeight'`, `'fontFamily'`, `'number'` are
 *     standard DTCG types; the exporter routes them to the matching transform.
 *   - `'string'` is NOT a DTCG standard type — it is this package's deliberate
 *     sentinel for an OPAQUE PASSTHROUGH value: gradients, box-shadows, px-unit
 *     typography strings, and the known-malformed hex. Such values carry their
 *     own units/format and emit verbatim as quoted constants, skipping every
 *     value transform. New transforms (e.g. a future px->dp/pt dimension
 *     transform) must leave `'string'` tokens alone.
 */
/**
 * Wrap a flat name→value record into DTCG leaves sharing one `$type`. The `const`
 * type parameters capture the literal value types (no `as const` needed at the
 * call site) so `values()` can hand them back to consumers losslessly.
 */
export function leaves($type, members) {
    return Object.fromEntries(Object.entries(members).map(([k, $value]) => [k, { $value, $type }]));
}
/**
 * Attach DTCG `$description` to each leaf in a group, looked up by the leaf's
 * Figma path (`${prefix}/${key}`) in a description map. Leaves with no entry are
 * returned unchanged. Used by the primitives layer to carry Figma's design-intent
 * descriptions onto the authored tree without touching any `$value`.
 */
export function described(prefix, group, descriptions) {
    return Object.fromEntries(Object.entries(group).map(([k, leaf]) => {
        const $description = descriptions[`${prefix}/${k}`];
        return [k, $description ? { ...leaf, $description } : leaf];
    }));
}
/** Project a leaf group down to its public `{ name: value }` shape, literals intact. */
export function values(group) {
    return Object.fromEntries(Object.entries(group).map(([k, leaf]) => [k, leaf.$value]));
}
/** Wrap a raw value (a direct hex/number, not a primitive reference) as a typed literal mode value. */
export function lit($value, $type = 'color') {
    return { $value, $type };
}
/**
 * Author a semantic token from its light + dark mode values. The primitive-ref
 * union `R` is recovered from the *string* arguments only (`Extract<…, string>`):
 * a token built solely from `lit()` objects has no string argument, so it yields
 * `SemanticToken<never>` (assignable into a `SemanticToken<PrimitiveRef>` map),
 * while a token written with primitive path strings carries them into `R`, so a
 * mistyped path (a string outside `PrimitiveRef`) still fails `tsc` at the
 * authoring site.
 */
export function semantic(light, dark, $type = 'color') {
    return { $type, light, dark };
}
//# sourceMappingURL=dtcg.js.map